'use es6';

import { useMemo, useCallback, useContext, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UITag from 'UIComponents/tag/UITag';
import Small from 'UIComponents/elements/Small';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import MobileFilterBar from 'marketplace-ui-common/components/mobile/MobileFilterBar';
import MobileTagButton from 'marketplace-ui-common/components/mobile/MobileTagButton';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import * as FilterKey from '../../constants/search/FilterKey';
import { getCertificationsRequestState, getCertifications } from '../../selectors/certifications';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { fromStateToFilterTags } from '../../utils/storefrontFilters';
import { translateFilterValue, getTranslatedSortText } from '../../utils/i18n';
import { getCredentialById } from '../../utils/credentials';
import { updateStorefrontFilters, DEFAULT_SORT } from '../../slices/storefrontFiltersSlice';
import { getServices } from '../../slices/serviceCatalogSlice';
import { getAccreditations, getAccreditationsRequestState } from '../../selectors/accreditations';
import { getIntegrations, getIntegrationsRequestState } from '../../slices/integrationsSlice';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const textPath = 'ecosystem-marketplace-solutions-ui.storefront';
const sortTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.sort';
const ResultsFilters = ({
  className,
  filters,
  onClearFilters,
  onCloseFilterTag,
  startSlot
}) => {
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const lang = getLang();
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const certifications = useSelector(getCertifications);
  const accreditations = useSelector(getAccreditations);
  const accreditationRequestState = useSelector(getAccreditationsRequestState);
  const catalogServices = useSelector(getServices);
  const integrations = useSelector(getIntegrations);
  const integrationsRequestState = useSelector(getIntegrationsRequestState);
  const accreditationsRequestSucceeded = useMemo(() => isSucceeded(accreditationRequestState), [accreditationRequestState]);
  const integrationsRequestSucceeded = useMemo(() => isSucceeded(integrationsRequestState), [integrationsRequestState]);
  const filterTagList = useMemo(() => {
    return fromStateToFilterTags(filters);
  }, [filters]);
  const getTranslatedCredential = useCallback((filterValue, credentialList) => {
    return getCredentialDisplayName(getCredentialById(credentialList, filterValue), lang);
  }, [lang]);
  const getIntegrationName = useCallback(filterValue => {
    return integrations.find(integration => integration.offeringId === parseInt(filterValue, 10)).displayName;
  }, [integrations]);
  const getFilterTranslation = useCallback((filterKey, filterValue) => {
    if (filterKey === FilterKey.CERTIFICATION) {
      return isSucceeded(certificationsRequestState) ? getTranslatedCredential(filterValue, certifications) : '';
    } else if (filterKey === FilterKey.ACCREDITATION) {
      return accreditationsRequestSucceeded ? getTranslatedCredential(filterValue, accreditations) : '';
    } else if (filterKey === FilterKey.INTEGRATION) {
      return integrationsRequestSucceeded ? getIntegrationName(filterValue) : '';
    } else if (filterKey === FilterKey.SERVICE) {
      const serviceFilter = catalogServices.filter(service => service.id === filterValue)[0];
      return filterValue && serviceFilter ? serviceFilter.name : '';
    } else {
      return translateFilterValue(filterKey, filterValue);
    }
  }, [certificationsRequestState, certifications, getIntegrationName, getTranslatedCredential, integrationsRequestSucceeded, accreditationsRequestSucceeded, accreditations, catalogServices]);
  const renderedFilterTags = useMemo(() => {
    return filterTagList.map(({
      key,
      value
    }) => {
      const translatedValue = getFilterTranslation(key, value);
      const tagText = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.filters.${key}.tag`,
        options: {
          value: translatedValue,
          count: translatedValue
        }
      });
      return /*#__PURE__*/_jsx(UITag, {
        className: "m-left-0 m-right-2 m-y-1",
        closeable: true,
        inline: true,
        onCloseClick: () => {
          onCloseFilterTag([key], [value]);
          trackInteraction('close filter tag', {
            filter: key,
            value
          });
        },
        children: tagText
      }, `${key}-${value}`);
    });
  }, [filterTagList, getFilterTranslation, onCloseFilterTag, trackInteraction]);
  const renderedMobileFilterTags = useMemo(() => {
    return filterTagList.map(({
      key,
      value
    }) => {
      const translatedValue = getFilterTranslation(key, value);
      const tagText = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.filters.${key}.tag`,
        options: {
          value: translatedValue,
          count: translatedValue
        }
      });
      return /*#__PURE__*/_jsx(MobileTagButton, {
        onClick: () => {
          onCloseFilterTag([key], [value]);
          trackInteraction('close filter tag', {
            filter: key,
            value
          });
        },
        closeable: true,
        children: tagText
      }, `${key}-${value}`);
    });
  }, [filterTagList, getFilterTranslation, onCloseFilterTag, trackInteraction]);
  const clearSort = useCallback(() => {
    dispatch(updateStorefrontFilters({
      updates: {
        [FilterKey.SORTS]: DEFAULT_SORT
      }
    }));
    trackInteraction('change sort', {
      sortField: DEFAULT_SORT
    });
  }, [dispatch, trackInteraction]);
  const sortTag = useMemo(() => {
    const sortKey = filters[FilterKey.SORTS];
    const tagText = `${I18n.text(`${sortTextPath}.filterTagPreface`)} ${getTranslatedSortText(sortKey)}`;
    return sortKey !== DEFAULT_SORT && /*#__PURE__*/_jsx(MobileTagButton, {
      onClick: clearSort,
      closeable: true,
      children: tagText
    });
  }, [clearSort, filters]);
  const defaultFilterBar = /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    wrap: "wrap",
    className: className,
    children: [renderedFilterTags, filterTagList.length > 0 && /*#__PURE__*/_jsx(UIButton, {
      className: "m-left-3",
      onClick: () => {
        onClearFilters();
        trackInteraction('clear all filters');
      },
      use: "link",
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.results.clearFilters`
        })
      })
    })]
  });
  return /*#__PURE__*/_jsx(MediaQuery, {
    minScreenSize: ScreenSizes.SM,
    alternate: /*#__PURE__*/_jsx(MobileFilterBar, {
      className: className,
      startSlot: startSlot,
      middleSlot: /*#__PURE__*/_jsxs(Fragment, {
        children: [renderedMobileFilterTags, sortTag]
      })
    }),
    children: defaultFilterBar
  });
};
ResultsFilters.propTypes = {
  className: PropTypes.string,
  filters: PropTypes.object.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onCloseFilterTag: PropTypes.func.isRequired,
  startSlot: PropTypes.node
};
export default ResultsFilters;